"use client";

/*
 * CONTRACT:
 *   PURPOSE: Интерактивный чек-лист соответствия 427-ФЗ для руководителя ЧОП.
 *   SCOPE:   Только UI-состояние (useState). Ничего не сохраняется на сервер.
 *   RULES:   Числа — font-mono + tabular-nums (BRAND гейт). radius = 0.
 */

import { useState } from "react";
import { Check } from "lucide-react";

const CHECKLIST_ITEMS = [
  {
    num: "01",
    npa: "427-ФЗ, лицензирование",
    text: "Проверьте дату окончания действующей лицензии и запланируйте переоформление на бессрочную после 01.09.2026.",
  },
  {
    num: "02",
    npa: "427-ФЗ, единый реестр охранников",
    text: "Сверьте всех охранников с реестром: у каждого должна быть активная запись; работников без записи — не допускать к сменам.",
  },
  {
    num: "03",
    npa: "427-ФЗ, страхование ответственности",
    text: "Заключите договор обязательного страхования профессиональной ответственности (после вступления закона).",
  },
  {
    num: "04",
    npa: "Локальные акты ЧОП",
    text: "Обновите внутренние регламенты и должностные инструкции под новые требования.",
  },
  {
    num: "05",
    npa: "427-ФЗ, права заказчика",
    text: "Заказчикам: добавьте требование о страховке и проверке охранников в реестре в тендерную документацию.",
  },
];

export function Fz427Checklist() {
  const [done, setDone] = useState<ReadonlySet<number>>(new Set<number>());

  const toggle = (i: number) => {
    setDone((prev) => {
      const next = new Set(prev);
      if (next.has(i)) {
        next.delete(i);
      } else {
        next.add(i);
      }
      return next;
    });
  };

  const total = CHECKLIST_ITEMS.length;
  const completed = done.size;
  const allDone = completed === total;

  return (
    <div>
      <div className="border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-surface))]">
        {CHECKLIST_ITEMS.map((item, i) => {
          const checked = done.has(i);
          return (
            <label
              key={item.num}
              className={`flex cursor-pointer items-start gap-4 border-b border-[hsl(var(--portal-border))] px-6 py-5 transition-colors last:border-b-0 ${
                checked ? "bg-[hsl(var(--portal-surface-2))]" : "hover:bg-[hsl(var(--portal-surface-2))]"
}`}
            >
              <span
                className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center border transition-colors ${
                  checked
                    ? "border-[hsl(var(--portal-accent))] bg-[hsl(var(--portal-accent))]"
                    : "border-[hsl(var(--portal-border-hover))] bg-transparent"
}`}
                aria-hidden="true"
              >
                {checked && <Check className="h-3.5 w-3.5 text-[hsl(var(--portal-accent-on))] [stroke-width:2.5]" />}
              </span>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(i)}
                className="sr-only"
                aria-label={item.text}
              />
              <span className="min-w-0">
                <span className="block font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--portal-text-muted))]">
                  {item.num} · {item.npa}
                </span>
                <span
                  className={`mt-1.5 block text-[15px] leading-relaxed ${
                    checked
                      ? "text-[hsl(var(--portal-text-muted))] line-through"
                      : "text-[hsl(var(--portal-text-secondary))]"
}`}
                >
                  {item.text}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      {/* Счётчик прогресса — mono-числа */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border border-[hsl(var(--portal-border))] bg-[hsl(var(--portal-bg-elevated))] px-5 py-3.5">
        <span className="font-mono text-[13px] text-[hsl(var(--portal-text-muted))]">
          Готово{" "}
          <b className={`font-bold tabular-nums ${allDone ? "text-[hsl(var(--portal-chart-2))]" : "text-[hsl(var(--portal-accent))]"}`}>
            {completed}
          </b>{" "}
          из{" "}
          <b className="font-bold tabular-nums text-[hsl(var(--portal-text))]">{total}</b>
        </span>
        {allDone && (
          <span className="inline-flex items-center gap-2 font-mono text-[13px] text-[hsl(var(--portal-chart-2))]">
            <i className="h-2 w-2 flex-none rounded-full bg-[hsl(var(--portal-chart-2))]" aria-hidden="true" />
            Базовая готовность к 01.09.2026
          </span>
        )}
      </div>
    </div>
  );
}